import { useEffect, useRef, useCallback } from 'react';

interface BottomSheetProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  /** Optional sticky footer at bottom of sheet */
  footer?: React.ReactNode;
}

export function BottomSheet({ open, onClose, title, children, footer }: BottomSheetProps) {
  const sheetRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [open]);

  const handleBackdropClick = useCallback((e: React.MouseEvent) => {
    if (e.target === backdropRef.current) {
      onCloseRef.current();
    }
  }, []);

  const handleContentPointerDown = useCallback((e: React.PointerEvent) => {
    e.stopPropagation();
  }, []);

  if (!open) return null;

  return (
    // z-[97] puts the sheet above the two navigation menus — SlideMenu is z-[90],
    // AvatarMenu z-[95]/[96] — and below Toast at z-[100]. It used to sit at
    // z-[80], the app's convention for page-level modals, which meant any sheet
    // opened *from* a menu rendered behind that menu's own full-screen backdrop:
    // it was mounted and interactive-looking in the DOM but completely invisible,
    // so "Trạng thái eKYC" and the eKYC lock prompt both read as a dead button.
    <div className="fixed inset-0 z-[97] flex items-end sm:items-center justify-center p-0 sm:p-4 overflow-hidden">
      <div
        ref={backdropRef}
        className="absolute inset-0 bg-black/50 animate-modal-backdrop"
        onClick={handleBackdropClick}
      />
      <div
        ref={sheetRef}
        onPointerDown={handleContentPointerDown}
        onClick={(e) => e.stopPropagation()}
        className="relative bg-white rounded-t-[28px] sm:rounded-2xl shadow-[0_-12px_40px_rgba(15,23,42,0.16)] w-full sm:max-w-lg max-h-[94dvh] sm:max-h-[88vh] flex flex-col animate-modal-slide-up overflow-hidden"
      >
        {/* Header — always visible */}
        <div className="shrink-0 flex items-center justify-between gap-3 px-4 sm:px-5 py-4 border-b border-slate-100 bg-white">
          <h3 className="text-[17px] font-bold text-slate-900 leading-tight text-safe-normal pr-2">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-400 transition-colors cursor-pointer shrink-0"
            aria-label="Đóng"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>
        {/* Body — scrolls internally */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden px-4 sm:px-5 py-4 min-h-0 overscroll-contain">
          {children}
        </div>
        {/* Footer — optional, sticky */}
        {footer && (
          <div className="shrink-0 border-t border-slate-100 px-4 sm:px-5 pt-3 safe-bottom-pad bg-white rounded-b-2xl">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
